import {
  bookingPaymentBalanceCents,
  bookingPaymentDueNowCents,
  bookingPaymentPolicyEligibility,
  serializeBookingPaymentPolicy,
} from "./bookingPaymentPolicy";
import { eventApprovalMode } from './eventApprovalV1';

export const PUBLIC_BOOKING_PAYMENT_FLOW_VERSION = "public_booking_payment_v1";
export const PUBLIC_BOOKING_PAYMENT_HOLD_MINUTES = 35;

function clean(value) {
  return String(value || "").trim().toLowerCase();
}

function isDeliveryMode(value) {
  return ["delivery", "home", "domicilio", "a_domicilio", "at_home"].includes(clean(value));
}

export function getPublicBookingPaymentPreview({
  service = {},
  isEventsBusiness = false,
  visitMode = "onsite",
  membershipApplied = false,
  eventApprovalFirstEnabled = false,
  eventTotalAmount = null,
  eventVisitType = null,
  business = {},
} = {}) {
  if ((business?.countryCode || 'PA') !== 'PA' || business?.onlinePaymentsEnabled === false) {
    service = {...service,bookingPaymentPolicy:{type:'none',enabled:false},depositPercent:0,depositAmount:0,requiredDepositAmount:0,paymentDepositAmount:0};
    eventApprovalFirstEnabled = isEventsBusiness;
  }
  const eventFlow = isEventsBusiness === true && eventApprovalFirstEnabled === true;
  if (eventFlow) {
    service=require('./eventExperienceServiceV1').eventExperienceService(service,eventVisitType);
    const type=clean(service.priceType || service.pricingType);
    if(['quote','from','cotizacion','cotización'].includes(type)) {
      return {requiresPayment:false,requiresQuote:true,requiresAccount:true,supported:!membershipApplied,approvalBeforePayment:true,policy:{enabled:false},serviceTotalCents:Math.round(Number(eventTotalAmount || 0)*100),dueNowCents:0,balanceCents:0,reason:membershipApplied?'membership-payment-policy-not-supported':null};
    }
    if(!Number.isFinite(eventTotalAmount) || eventTotalAmount<0) {
      return {requiresPayment:true,supported:false,reason:'event-price-review-required',policy:{enabled:false},dueNowCents:0,balanceCents:0};
    }
  }
  const pricedService=isEventsBusiness && Number.isFinite(eventTotalAmount) && eventTotalAmount>=0 ? {...service,price:eventTotalAmount,priceType:'fixed'} : service;
  const policy = serializeBookingPaymentPolicy(pricedService || {});
  const eligibility = bookingPaymentPolicyEligibility(pricedService || {});
  const dueNowCents = bookingPaymentDueNowCents(pricedService || {});
  const balanceCents = bookingPaymentBalanceCents(pricedService || {});
  const serviceTotalCents = Math.max(0, Number(eligibility.priceCents || 0));
  const requiresPayment = policy.enabled === true && dueNowCents > 0;

  let supported = true;
  let reason = null;

  if (requiresPayment && eligibility.eligible !== true) {
    supported = false;
    reason = eligibility.reason || "payment-policy-not-supported";
  } else if (requiresPayment && isEventsBusiness === true && !eventFlow) {
    supported = false;
    reason = "events-payment-policy-not-supported";
  } else if (requiresPayment && isDeliveryMode(visitMode) && !eventFlow) {
    supported = false;
    reason = "delivery-payment-policy-not-supported";
  } else if ((requiresPayment || eventFlow) && membershipApplied === true) {
    supported = false;
    reason = "membership-payment-policy-not-supported";
  }

  return {
    flowVersion: PUBLIC_BOOKING_PAYMENT_FLOW_VERSION,
    holdMinutes: PUBLIC_BOOKING_PAYMENT_HOLD_MINUTES,
    policy,
    requiresPayment,
    supported,
    reason,
    serviceTotalCents,
    dueNowCents,
    balanceCents,
    approvalBeforePayment: eventFlow && eventApprovalMode(business,service)==='manual',
    requiresAccount:eventFlow,
  };
}

export function publicBookingPaymentUnsupportedMessage(reason) {
  const key = clean(reason);
  if(key==='event-price-review-required')return 'El negocio debe confirmar la cotización antes de habilitar la reserva con abono.';
  if (key === "events-payment-policy-not-supported") {
    return "El pago obligatorio para reservar eventos todavía no está habilitado.";
  }
  if (key === "delivery-payment-policy-not-supported") {
    return "El pago para reservar a domicilio todavía no está habilitado para este servicio.";
  }
  if (key === "membership-payment-policy-not-supported") {
    return "Todavía no se puede combinar una membresía con un pago obligatorio para reservar.";
  }
  if (key === "fixed-price-required") {
    return "Este servicio necesita un precio fijo para exigir pago al reservar.";
  }
  if (key === "positive-price-required") {
    return "Este servicio necesita un precio mayor a cero para exigir pago al reservar.";
  }
  return "Esta opción de pago todavía no está disponible para esta reserva.";
}

export function publicServicePaymentLabel(service = {}) {
  const preview = getPublicBookingPaymentPreview({ service });
  if (!preview.requiresPayment) return "Sin anticipo";
  if (!preview.supported) return "Consulta las condiciones de reserva";
  if (preview.policy.type === "full") return "Pago completo al reservar";
  const amount = new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 2,
  }).format(preview.dueNowCents / 100);
  return `Reserva con ${amount}`;
}

export function isServerRequiredPaymentBooking(booking = {}) {
  const status = clean(booking.status || booking.confirmationStatus);
  const requirementStatus = clean(booking.paymentRequirementStatus);
  const requiredCents = Math.max(
    0,
    Math.round(
      Number(
        booking.requiredBookingPaymentAmountInCents ??
        (Number(booking.requiredBookingPaymentAmount || 0) * 100)
      ) || 0
    )
  );

  return (
    booking.requiresPaymentBeforeConfirmation === true &&
    status === "pending_payment" &&
    requirementStatus === "pending" &&
    requiredCents > 0
  );
}

export function buildRequiredPaymentSessionRequest({
  businessId,
  bookingId,
  preview,
} = {}) {
  const amountCents = Math.max(0, Number(preview?.dueNowCents || 0));

  return {
    businessId: String(businessId || "").trim(),
    bookingId: String(bookingId || "").trim(),
    mode: "deposit",
    amount: Math.round(amountCents) / 100,
  };
}

export function requiredPaymentButtonLabel(preview = {}, isSaving = false) {
  if(preview.requiresQuote && preview.supported)return isSaving ? 'Enviando solicitud…' : 'Solicitar cotización';
  if(preview.approvalBeforePayment && preview.supported)return isSaving ? 'Enviando solicitud…' : 'Solicitar reserva';
  if (isSaving) return "Preparando pago...";
  if (preview.requiresPayment !== true) return "Confirmar reserva";
  if (preview.supported !== true) return "Pago no disponible";
  return "Continuar al pago";
}
